import { MeasurementsStoreActionType } from '@dynamika-shared/types/measurements';
import {
  COLOR_LOG,
  LOG_SCORING_FORM_EMITTER,
} from '@dynamika-shared/utils/development';
import { notifyScoringFormsAction } from 'src/store-redux/genericActions';
import { store } from 'src/store-redux/store';
import { formRpcScheme } from 'src/services/rpc/form/scheme';

export enum ScoringFormActionNames {
  MEASUREMENTS_CHANGE = 'measurementChanges',
  UPDATE_FORM = 'updateForm',
  FETCH_TASK_DONE = 'fetchTaskDone',
  SERIES_CHANGED = 'seriesChanged',
  SEGMENTATION_CHANGED = 'segmentationChanged',
  SELECTED_LABELS = 'selectedLabels',
  VIEWER_BUSY = 'viewerBusy',
}

export function scoringFormMeasurementsChangeAction(
  scoringCode: string | undefined,
  action: 'init' | MeasurementsStoreActionType,
  {
    code,
    locked = false,
    value = null,
    parentMeasurementId,
    appendAnnotation,
    removeAnnotation,
    entry,
    appendPending,
    retriable,
    lockedByRemoteTab,
  }: {
    code: string;
    locked?: boolean;
    value?: unknown;
    parentMeasurementId?: string;
    appendAnnotation?: unknown;
    removeAnnotation?: unknown;
    // D7-6906: multi-annotation context surfaced to the form.
    entry?: { instrument: string; index: number };
    appendPending?: number;
    retriable?: boolean;
    lockedByRemoteTab?: boolean;
  }
) {
  if (!scoringCode) {
    return;
  }
  // Same payload for both legs: the Redux notification (logFormEvent) and the
  // RPC bus (formEvents). Previously the bus
  // carried only {code, action, locked, value}, so a detached/spawn tab's form
  // never saw the D7-6906 multi-annotation context (parentMeasurementId, append/
  // removeAnnotation, entry, appendPending, retriable, lockedByRemoteTab).
  const changesPayload = {
    code,
    action,
    locked,
    value,
    ...(parentMeasurementId !== undefined && { parentMeasurementId }),
    ...(appendAnnotation !== undefined && { appendAnnotation }),
    ...(removeAnnotation !== undefined && { removeAnnotation }),
    ...(entry !== undefined && { entry }),
    ...(appendPending !== undefined && { appendPending }),
    ...(retriable !== undefined && { retriable }),
    ...(lockedByRemoteTab !== undefined && { lockedByRemoteTab }),
  };
  logFormEvent(scoringCode, ScoringFormActionNames.MEASUREMENTS_CHANGE, changesPayload);
  formRpcScheme.events.measurementChanges(changesPayload);
}

export function scoringFormFetchTaskDoneAction(
  scoringCode: string | undefined,
  data: object
) {
  if (!scoringCode) {
    return;
  }

  logFormEvent(scoringCode, ScoringFormActionNames.FETCH_TASK_DONE, { ...data });
  formRpcScheme.events.fetchTaskDone({ ...data });
}

/**
 * Announce the change on the host side.
 *
 * Kept as a Redux dispatch so the change shows up in devtools alongside
 * everything else that happened in the same turn; the delivery to the form
 * itself is the addressed `formRpcScheme.events.*` emit next to each call.
 */
function logFormEvent(
  formCode: string,
  actionName: ScoringFormActionNames,
  value: unknown
) {
  if (LOG_SCORING_FORM_EMITTER) {
    COLOR_LOG('peach', formCode, actionName, value);
  }

  store.dispatch(notifyScoringFormsAction({ formCode, actionName, value }));
}

export function scoringFormUpdateAction(
  scoringCode: string | undefined,
  data: object
) {
  if (!scoringCode) {
    return;
  }

  logFormEvent(scoringCode, ScoringFormActionNames.UPDATE_FORM, { ...data });
  formRpcScheme.events.updateForm({ ...data });
}

export function scoringFormSeriesChangedAction(
  scoringCode: string | undefined,
  data: {
    studyInstanceUID: string;
    seriesInstanceUID: string;
    viewportId: string;
    seriesLabel?: string;
  }[]
) {
  if (!scoringCode) {
    return;
  }

  logFormEvent(scoringCode, ScoringFormActionNames.SERIES_CHANGED, data);
  formRpcScheme.events.seriesChanged(data);
}

export function scoringFormSegmentationChangedAction(
  scoringCode: string | undefined,
  data: unknown
) {
  if (!scoringCode) {
    return;
  }

  logFormEvent(scoringCode, ScoringFormActionNames.SEGMENTATION_CHANGED, data);
  formRpcScheme.events.segmentationChanged(data);
}
export function scoringFormSelectedLabelsAction(
  scoringCode: string | undefined,
  data: unknown
) {
  if (!scoringCode) {
    return;
  }

  logFormEvent(scoringCode, ScoringFormActionNames.SELECTED_LABELS, data);
  formRpcScheme.events.selectedLabels(data);
}

export function scoringFormViewerBusyChangedAction(
  scoringCode: string | undefined,
  data: unknown
) {
  if (!scoringCode) {
    return;
  }

  logFormEvent(scoringCode, ScoringFormActionNames.VIEWER_BUSY, data);
  formRpcScheme.events.viewerBusy(data);
}
